// src/components/FPMSConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem,
    FormControl,
    InputLabel,
    Select,
    Chip // Import Chip for deletable roles
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../firebase"; // Asumsi path ke firebase.js Anda
import { ref, push, set, onValue, update, remove, query, orderByChild, equalTo } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function FPMSConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // State tambahan untuk user data di dialog (tidak digunakan di FPMSConfig, tapi dipertahankan untuk konsistensi)
    const [userGender, setUserGender] = useState('');
    const [initialUserGender, setInitialUserGender] = useState('');
    const [userJabatan, setUserJabatan] = useState('');
    const [initialUserJabatan, setInitialUserJabatan] = useState('');
    const [userStatusPekerjaan, setUserStatusPekerjaan] = useState('');
    const [initialUserStatusPekerjaan, setInitialUserStatusPekerjaan] = useState('');
    const [userStatusTenagaKerja, setUserStatusTenagaKerja] = useState('');
    const [initialUserStatusTenagaKerja, setInitialUserStatusTenagaKerja] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userAvatar, setUserAvatar] = useState('');
    const [userIndex, setUserIndex] = useState('');
    const [userDepartment, setUserDepartment] = useState('');
    const [initialUserDepartment, setInitialUserDepartment] = useState('');

    // New states for Roles (tidak digunakan di FPMSConfig, tapi dipertahankan untuk konsistensi)
    const [userRoles, setUserRoles] = useState([]);
    const [initialUserRoles, setInitialUserRoles] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [selectedRolesInDialog, setSelectedRolesInDialog] = useState([]);

    // State for role chip delete confirmation (tidak digunakan di FPMSConfig, tapi dipertahankan untuk konsistensi)
    const [openRoleDeleteConfirmDialog, setOpenRoleDeleteConfirmDialog] = useState(false);
    const [roleToDeleteId, setRoleToDeleteId] = useState(null);
    const [roleToDeleteName, setRoleToDeleteName] = useState('');


    const [departments, setDepartments] = useState([]); // Tidak digunakan di FPMSConfig
    const [jabatanList, setJabatanList] = useState([]); // Tidak digunakan di FPMSConfig
    const [statusPekerjaanList, setStatusPekerjaanList] = useState([]); // Digunakan untuk Status Lokasi
    const [statusTenagaKerjaList, setStatusTenagaKerjaList] = useState([]); // Tidak digunakan di FPMSConfig

    // State untuk mode detail dan edit di dialog user (tidak digunakan di FPMSConfig, tapi dipertahankan untuk konsistensi)
    const [isDetailView, setIsDetailView] = useState(false);
    const [isEditableDetail, setIsEditableDetail] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

    // FPMS specific checks
    const isStatusLokasiTable = dbPath === "db_config/db_fresh_pineapple/tb_status_lokasi";
    const isKodeWilayahTable = dbPath === "db_config/db_fresh_pineapple/tb_kode_wilayah"; // New check for Kode Wilayah table

    // New state for Kepala Wilayah dropdown
    const [kepalaWilayahList, setKepalaWilayahList] = useState([]);
    const [selectedKepalaWilayah, setSelectedKepalaWilayah] = useState(''); // State for selected Kepala Wilayah ID

    // New state for Color Picker
    const [selectedColor, setSelectedColor] = useState('#FFFFFF'); // Default to white


    // Ambil data dari Firebase
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(
            tbRef,
            (snapshot) => {
                const data = snapshot.val();
                if (snapshot.exists()) {
                    // Untuk FPMS, semua tabel adalah tabel konfigurasi sederhana
                    // Handle primaryKeyField for all tables, including Kode Wilayah
                    const list = Object.keys(data).map(key => ({
                        [primaryKeyField]: key,
                        ...data[key]
                    }));
                    setDataList(list);
                } else {
                    setDataList([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal memuat data ${title.toLowerCase()}.`,
                    severity: "error",
                });
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [dbPath, title, primaryKeyField]); // Added primaryKeyField to dependencies

    // Fetch Status Lokasi and Kepala Wilayah for dropdowns
    useEffect(() => {
        const fetchConfigData = async () => {
            if (isStatusLokasiTable) {
                const statusLokasiRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiSnapshot = await onValuePromise(statusLokasiRef);
                const statusList = statusLokasiSnapshot.val();
                if (statusList) {
                    const statusLokasiList = Object.keys(statusList).map(key => ({
                        id: key,
                        name: statusList[key].nama_status_lokasi, // Sesuaikan dengan fieldName di tb_status_lokasi
                        indicator: statusList[key].indicator || '#FFFFFF' // Fetch indicator
                    }));
                    setStatusPekerjaanList(statusLokasiList); // Re-use state for simplicity, consider renaming
                }
            }

            // New: Fetch Kepala Wilayah
            if (isKodeWilayahTable) {
                const kepalaWilayahRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_wilayah");
                const kepalaWilayahSnapshot = await onValuePromise(kepalaWilayahRef);
                const kepalaWilayahData = kepalaWilayahSnapshot.val();
                if (kepalaWilayahData) {
                    const list = Object.keys(kepalaWilayahData).map(key => ({
                        id: key,
                        name: kepalaWilayahData[key].nama_kepala_wilayah
                    }));
                    setKepalaWilayahList(list);
                }
            }
        };

        fetchConfigData();
    }, [isStatusLokasiTable, isKodeWilayahTable]);


    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setFieldValue("");
        setSelectedKepalaWilayah(''); // Clear selected Kepala Wilayah on new
        setSelectedColor('#FFFFFF'); // Clear selected color on new
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFieldValue("");
        setIsEditing(false);
        setCurrentId(null);
        setSelectedKepalaWilayah(''); // Clear selected Kepala Wilayah on close
        setSelectedColor('#FFFFFF'); // Clear selected color on close
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        if (!fieldValue.trim()) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }

        const normalizedFieldValue = fieldValue.toLowerCase().trim();

        const isDuplicate = dataList.some(item =>
            item[fieldName]?.toLowerCase().trim() === normalizedFieldValue &&
            item[primaryKeyField] !== currentId
        );

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} "${fieldValue}" sudah ada.`,
                severity: "error",
            });
            return;
        }

        try {
            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentId}`);
                const updatedData = { [fieldName]: fieldValue };
                if (isKodeWilayahTable) {
                    updatedData.id_kepala_wilayah = selectedKepalaWilayah; // Save selected Kepala Wilayah ID
                }
                if (isStatusLokasiTable) {
                    updatedData.indicator = selectedColor; // Save selected color for Status Lokasi
                }
                await update(itemRef, updatedData);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                const tbRef = ref(database, dbPath);
                const newRef = push(tbRef);
                const key = newRef.key;
                const data = {
                    [primaryKeyField]: key,
                    [fieldName]: fieldValue,
                };
                if (isKodeWilayahTable) {
                    data.id_kepala_wilayah = selectedKepalaWilayah; // Save selected Kepala Wilayah ID
                }
                if (isStatusLokasiTable) {
                    data.indicator = selectedColor; // Save selected color for Status Lokasi
                }
                await set(newRef, data);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil disimpan.`,
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);

                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};

                const existingFieldValues = Object.values(existingData).map(item => item[fieldName]?.toLowerCase().trim());

                let importedCount = 0;
                let ignoredCount = 0;

                // New: Map for Kepala Wilayah for import lookup
                const kepalaWilayahMap = {};
                kepalaWilayahList.forEach(kw => kepalaWilayahMap[kw.name.toLowerCase()] = kw.id);

                for (const item of jsonData) {
                    let importedValue = item[title.slice(title.indexOf(" ") + 1)];
                    if (!importedValue) {
                        ignoredCount++;
                        continue;
                    }
                    const dataToSave = { [fieldName]: importedValue };

                    const valueLower = importedValue.toLowerCase().trim();
                    if (existingFieldValues.includes(valueLower)) {
                        ignoredCount++;
                        continue;
                    }
                    
                    // New: Handle id_kepala_wilayah for Kode Wilayah table during import
                    if (isKodeWilayahTable && item["Kepala Wilayah"]) {
                        const kepalaWilayahId = kepalaWilayahMap[item["Kepala Wilayah"].toLowerCase()];
                        if (kepalaWilayahId) dataToSave.id_kepala_wilayah = kepalaWilayahId;
                    }
                    // New: Handle Indicator for Status Lokasi table during import
                    if (isStatusLokasiTable && item["Indicator"]) {
                        dataToSave.indicator = item["Indicator"];
                    }

                    const newRef = push(tbRef);
                    const key = newRef.key;
                    dataToSave[primaryKeyField] = key;
                    await set(newRef, dataToSave);
                    importedCount++;
                }

                setSnackbar({
                    open: true,
                    message: `Berhasil impor ${importedCount} data baru. ${ignoredCount} data sudah ada/invalid diabaikan.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal impor Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal mengimpor data dari Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };

        reader.readAsArrayBuffer(file);
    };


    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "Tidak ada data untuk diekspor.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            row[primaryKeyField] = item[primaryKeyField];
            row[title.slice(title.indexOf(" ") + 1)] = item[fieldName];
            // New: Export Kepala Wilayah name for Kode Wilayah table
            if (isKodeWilayahTable && item.id_kepala_wilayah) {
                row["Kepala Wilayah"] = kepalaWilayahList.find(kw => kw.id === item.id_kepala_wilayah)?.name || item.id_kepala_wilayah;
            }
            // New: Export Indicator for Status Lokasi table
            if (isStatusLokasiTable && item.indicator) {
                row["Indicator"] = item.indicator;
            }
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentId(row[primaryKeyField]);
        setFieldValue(row[fieldName]);
        if (isKodeWilayahTable) {
            setSelectedKepalaWilayah(row.id_kepala_wilayah || ''); // Set existing Kepala Wilayah ID
        }
        if (isStatusLokasiTable) {
            setSelectedColor(row.indicator || '#FFFFFF'); // Set existing indicator color
        }
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;

        try {
            const idToDelete = selectedRow[primaryKeyField];
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal hapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            <TableCell>Id</TableCell>
                            <TableCell>{title.slice(title.indexOf(" ") + 1)}</TableCell>
                            {isKodeWilayahTable && <TableCell>Kepala Wilayah</TableCell>} {/* New Header for Kode Wilayah */}
                            {isStatusLokasiTable && <TableCell>Indicator</TableCell>} {/* New Header for Indicator */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row[primaryKeyField]}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{row[primaryKeyField]}</TableCell>
                                        <TableCell>
                                            {isStatusLokasiTable && (row.indicator) ? (
                                                <Chip
                                                    label={row[fieldName]}
                                                    style={{ backgroundColor: row.indicator, color: '#FFFFFF' }} // Use indicator color
                                                    size="small"
                                                />
                                            ) : (
                                                row[fieldName]
                                            )}
                                        </TableCell>
                                        {isKodeWilayahTable && (
                                            <TableCell>
                                                {kepalaWilayahList.find(kw => kw.id === row.id_kepala_wilayah)?.name || 'N/A'}
                                            </TableCell>
                                        )} {/* Display Kepala Wilayah Name */}
                                        {isStatusLokasiTable && (
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: row.indicator || '#FFFFFF', border: '1px solid #ccc' }} />
                                                    <Typography variant="body2">{row.indicator || 'N/A'}</Typography>
                                                </Box>
                                            </TableCell>
                                        )} {/* Display Indicator Color */}
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={isKodeWilayahTable ? 4 : (isStatusLokasiTable ? 4 : 3)} align="center"> {/* Adjusted colspan */}
                                    Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={title.slice(title.indexOf(" ") + 1)}
                        type="text"
                        fullWidth
                        value={fieldValue}
                        onChange={(e) => setFieldValue(e.target.value)}
                        sx={{ mb: 2 }} // Add margin bottom
                    />
                    {isKodeWilayahTable && (
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Kepala Wilayah</InputLabel>
                            <Select
                                value={selectedKepalaWilayah}
                                onChange={(e) => setSelectedKepalaWilayah(e.target.value)}
                                label="Kepala Wilayah"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kepalaWilayahList.map((kw) => (
                                    <MenuItem key={kw.id} value={kw.id}>{kw.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {isStatusLokasiTable && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                            <TextField
                                margin="dense"
                                label="Color"
                                type="text"
                                value={selectedColor}
                                onChange={(e) => setSelectedColor(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            />
                            <input
                                type="color"
                                value={selectedColor}
                                onChange={(e) => setSelectedColor(e.target.value)}
                                style={{ width: 50, height: 50, border: 'none', padding: 0, cursor: 'pointer' }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Update" : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Data Tabel Utama */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {title.slice(title.indexOf(" ") + 1).toLowerCase()} "{selectedRow?.[fieldName]}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default FPMSConfig;
